<template>
  <div
    :class="isOnTopOfContactForm ? $style.contactFormAgencyCard : $style.listingSectionAgencyCard"
  >
    <div
      v-if="!!listerLogo"
      :class="$style.agentColoredAgencyImageSection"
      :style="{ background: agencyAgent?.logoBackgroundColor || '' }"
    >
      <a
        v-if="agencyProfileLink"
        :href="agencyProfileLink"
        :title="t('listerBranding.agencyProfileLinkText')"
        @click="onAgencyProfileLinkClicked(InteractionContainerName.LOGO)"
      >
        <ListItemLogo :class="$style.listItemLogo" :imageSrc="listerLogo" />
      </a>
      <ListItemLogo v-else :class="$style.listItemLogo" :imageSrc="listerLogo" />
    </div>
    <div v-if="!isOnTopOfContactForm" :class="$style.agencyProfile">
      <span :class="$style.agencyName">
        {{ listerName }}
      </span>
      <span>
        {{ listerStreet }} {{ listerStreetAddition }} {{ listerZipCode }} {{ listerLocality }}
      </span>
      <span>
        <a
          v-if="agencyProfileLink"
          :href="agencyProfileLink"
          :title="t('listerBranding.agencyProfileLinkText')"
          @click="onAgencyProfileLinkClicked(InteractionContainerName.LINK)"
          >{{ t('listerBranding.agencyProfileLinkText') }}</a
        >
      </span>
    </div>
    <div :class="$style.agentProfile">
      <AgentAvatar
        :avatarUrl="agencyAgent?.avatarUrl"
        :firstName="agencyAgent?.firstName || ''"
        :lastName="agencyAgent?.lastName || ''"
      />
      <div :class="$style.agentInfos">
        <span :class="$style.agentName">
          {{ agencyAgent?.firstName }} {{ agencyAgent?.lastName }}
        </span>
        <span v-if="!isOnTopOfContactForm" :class="$style.agentJobTitle">
          {{ agencyAgent?.jobTitle }}
        </span>
        <PhoneNumber
          v-if="agencyAgent?.mobileNumber"
          :label="t('viewingDetails.phoneButton.label')"
          :buttonTheme="phoneButtonTheme"
          :phoneNumber="agencyAgent?.mobileNumber"
          :class="$style.phoneButton"
          @mobileNumberClick="phoneNumberClickMobile(PhoneNumberType.LISTER_MOBILE)"
          @numberClick="revealedPhoneNumberClick(PhoneNumberType.LISTER_MOBILE)"
          @numberReveal="phoneNumberReveal(PhoneNumberType.LISTER_MOBILE)"
        />
        <PhoneNumber
          v-if="shouldShowPhoneNumber"
          :label="t('viewingDetails.phoneButton.label')"
          :buttonTheme="phoneButtonTheme"
          :phoneNumber="agencyAgent?.phoneNumber || ''"
          :class="$style.phoneButton"
          @mobileNumberClick="phoneNumberClickMobile(PhoneNumberType.LISTER_PHONE)"
          @numberClick="revealedPhoneNumberClick(PhoneNumberType.LISTER_PHONE)"
          @numberReveal="phoneNumberReveal(PhoneNumberType.LISTER_PHONE)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ButtonTheme } from '@smg-real-estate/domus';
import { Listing } from '@smg-real-estate/hg-rets-schema';
import { AgencyAgent, HgSupportedLanguage, ListerBranding } from '@smg-real-estate/hg-search-libs';
import { PhoneNumber } from '@smg-real-estate/seeker-app-shared-components';
import { computed } from 'vue';

import AgentAvatar from '@/components/AgentAvatar/AgentAvatar.vue';
import { PhoneNumberType } from '@/components/ContactForm/contact-form-tracking-events';
import ListItemLogo from '@/components/ListItemLogo/ListItemLogo.vue';
import { InteractionContainerName } from '@/helpers/GTM/GTMParameters';
import { createAgencyProfilePageRoute } from '@/views/AgencyProfilePage/agencyProfilePageRoutingHelpers';
import { trackAgencyProfileLinkClicked } from '@/views/AgencyProfilePage/agencyProfileTracking';
import { useI18n } from 'vue-i18n';

defineOptions({
  name: 'AgencyCard',
});

const { locale, t } = useI18n();

const emit =
  defineEmits<
    (
      e: 'phoneNumberClickMobile' | 'revealedPhoneNumberClick' | 'phoneNumberReveal',
      phoneNumberType: PhoneNumberType,
    ) => void
  >();

export interface Props {
  listing: Listing;
  agencyAgent: AgencyAgent | null;
  listerBranding: ListerBranding | null;
  isOnTopOfContactForm: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  agencyAgent: null,
  listerBranding: null,
});

const agencyProfileLink = computed(() => {
  const urlKeyword = props.listerBranding?.profilePageUrlKeyword;
  return urlKeyword && props.listing.lister?.id
    ? createAgencyProfilePageRoute(
        locale.value as HgSupportedLanguage,
        urlKeyword,
        props.listing.lister.id,
      )
    : undefined;
});

const listerName = computed(() => props.listing.lister?.legalName);
const listerLogo = computed(() => props.listing.lister?.logoUrl);
const listerZipCode = computed(() => props.listing.lister?.address?.postalCode);
const listerLocality = computed(() => props.listing.lister?.address?.locality);
const listerStreet = computed(() => {
  const street = props.listing.lister?.address?.street;
  if (!street) {
    return '';
  }

  if (!listerStreetAddition.value && !listerZipCode.value && !listerLocality.value) {
    return `${street}`;
  } else {
    return `${street},`;
  }
});
const listerStreetAddition = computed(() => {
  const streetAddition = props.listing.lister?.address?.streetAddition;
  if (!streetAddition) {
    return '';
  }
  if (!listerZipCode.value && !listerLocality.value) {
    return `${streetAddition}`;
  } else {
    return `${streetAddition},`;
  }
});

const phoneButtonTheme = computed(() => {
  if (props.isOnTopOfContactForm) {
    return ButtonTheme.SecondaryBordered;
  } else {
    return ButtonTheme.Secondary;
  }
});
const shouldShowPhoneNumber = computed(() => {
  if (props.isOnTopOfContactForm) {
    // Only one number is shown in the premium position, preferably mobile
    return !!(!props.agencyAgent?.mobileNumber && props.agencyAgent?.phoneNumber);
  } else {
    return (
      !!props.agencyAgent?.phoneNumber &&
      props.agencyAgent?.phoneNumber !== props.agencyAgent?.mobileNumber
    );
  }
});

function onAgencyProfileLinkClicked(
  containerName: InteractionContainerName.LINK | InteractionContainerName.LOGO,
): void {
  if (agencyProfileLink.value) {
    trackAgencyProfileLinkClicked(containerName, agencyProfileLink.value);
  }
}
function phoneNumberClickMobile(phoneNumberType: PhoneNumberType): void {
  emit('phoneNumberClickMobile', phoneNumberType);
}
function revealedPhoneNumberClick(phoneNumberType: PhoneNumberType): void {
  emit('revealedPhoneNumberClick', phoneNumberType);
}
function phoneNumberReveal(phoneNumberType: PhoneNumberType): void {
  emit('phoneNumberReveal', phoneNumberType);
}
</script>

<style lang="scss" module>
@import '@/assets/variables';

.listingSectionAgencyCard {
  border: 1px solid $color-grey-50;
  border-radius: 4px;
}

.contactFormAgencyCard {
  border-bottom: 1px solid $color-grey-50;
  border-radius: 0;
}

.agentColoredAgencyImageSection {
  position: relative;
  height: 60px;
  border-bottom: 1px solid $color-grey-50;
  border-radius: 4px 4px 0 0;

  img {
    position: absolute;
    top: 50%;
    left: 50%;
    max-width: 120px;
    max-height: 44px;
    margin: 0;
    transform: translate(-50%, -50%);
  }
}

.agencyProfile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: $space-100 $space-100 $space-50;
  border-bottom: 1px solid $color-grey-50;

  .agencyName {
    font-weight: bold;
  }

  span {
    line-height: $space-150;

    a {
      line-height: $space-200;
    }
  }
}

.agentProfile {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: $space-100 $space-100 $space-50;

  .agentInfos {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-left: $space-75;

    .agentName {
      font-weight: bold;
      line-height: $space-150;
    }

    .phoneButton {
      line-height: $space-150;

      &:first-of-type {
        padding-top: $space-50;
      }
    }
  }
}
</style>
